/**
 * Kommunens egen sida om skolval eller förskoleplats.
 *
 * Regler (Jimmy, 16 okt 2026):
 * - Vi sammanfattar aldrig kommunens regler med egna ord.
 * - Perioden visas bara om kommunen anger den med datum, ordagrant citerad.
 * - Är uppgiften äldre än sex månader visas bara länken — en gammal
 *   ansökningsperiod är värre än ingen.
 * - En period som redan har passerat visas inte heller.
 * Klientsäker: ren logik.
 */
export type ChoiceInfo = {
  municipality_code: string;
  municipality_name: string;
  kind: "forskola" | "grundskola";
  page_url: string;
  e_service_name: string | null;
  e_service_url: string | null;
  period_quote: string | null;
  opens_on: string | null;
  closes_on: string | null;
  read_on: string;
};

export const MAX_ALDER_MANADER = 6;
/** Larmet går en månad före sexmånadersregeln, så att vi hinner läsa om. */
export const LARM_ALDER_MANADER = 5;

/** Sant om uppgiften är högst `manader` månader gammal den givna dagen. */
export function inomManader(readOn: string, manader: number, idag: Date = new Date()): boolean {
  const d = new Date(`${readOn}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return false;
  const grans = new Date(Date.UTC(idag.getUTCFullYear(), idag.getUTCMonth() - manader, idag.getUTCDate()));
  return d >= grans;
}

/** Sant om uppgiften är högst sex månader gammal den givna dagen. */
export function arFarsk(readOn: string, idag: Date = new Date()): boolean {
  return inomManader(readOn, MAX_ALDER_MANADER, idag);
}

export type KommunAlder = { municipality_code: string; municipality_name: string; read_on: string; larm: boolean };

/** En rad per kommun med äldsta läsdatum; larm när den passerat fem månader. */
export function kommunAldrar(rows: Pick<ChoiceInfo, "municipality_code" | "municipality_name" | "read_on">[], idag: Date = new Date()): KommunAlder[] {
  const per = new Map<string, KommunAlder>();
  for (const r of rows) {
    const f = per.get(r.municipality_code);
    const kod = r.municipality_code;
    const rad: KommunAlder = { ...r, larm: false };
    if (!f || r.read_on < f.read_on) per.set(kod, rad);
  }
  return [...per.values()]
    .map((k) => ({ ...k, larm: !inomManader(k.read_on, LARM_ALDER_MANADER, idag) }))
    .sort((a, b) => a.read_on.localeCompare(b.read_on) || a.municipality_name.localeCompare(b.municipality_name, "sv"));
}

/** Perioden som får visas, eller null. */
export function visadPeriod(info: ChoiceInfo, idag: Date = new Date()): string | null {
  if (!info.period_quote) return null;
  if (!arFarsk(info.read_on, idag)) return null;
  if (info.closes_on && new Date(`${info.closes_on}T23:59:59Z`) < idag) return null;
  return info.period_quote;
}

export function lastDatum(readOn: string): string {
  return new Intl.DateTimeFormat("sv-SE", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }).format(
    new Date(`${readOn}T00:00:00Z`),
  );
}
